import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { Avatar } from '@/components/common/Avatar'
import { useBatch } from '@/hooks/useBatches'
import { roomService, type Room, type RoomAssignment, type BatchEnrollment } from '@/services/roomService'
import { AdminBatchSchedulesTab } from './AdminBatchSchedulesTab'

// Flattened state for the rooms admin UI.
interface PageState {
    rooms: Room[]
    assignments: RoomAssignment[]
    enrollments: BatchEnrollment[]
    isLoading: boolean
    error: string | null
    warnings: string[]
}

interface AddAssignmentModal {
    roomId: string
    roomNumber: string
}

const initialState: PageState = {
    rooms: [],
    assignments: [],
    enrollments: [],
    isLoading: true,
    error: null,
    warnings: [],
}

export const AdminBatchRoomsPage: React.FC = () => {
    const { id: batchId } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { data: batchData } = useBatch(batchId || '')
    const [state, setState] = useState<PageState>(initialState)
    const [confirmOverride, setConfirmOverride] = useState(false)
    const [customRoomNames, setCustomRoomNames] = useState('')
    const [addModal, setAddModal] = useState<AddAssignmentModal | null>(null)
    const [addEnrollmentId, setAddEnrollmentId] = useState('')
    const [addNotes, setAddNotes] = useState('')
    const [addError, setAddError] = useState<string | null>(null)

    const [capacityModal, setCapacityModal] = useState<{ room: Room, value: number } | null>(null)
    const [renameModal, setRenameModal] = useState<Room | null>(null)
    const [renameValue, setRenameValue] = useState('')
    const [isUpdating, setIsUpdating] = useState(false)
    const [activeTab, setActiveTab] = useState<'rooms' | 'schedules'>('rooms')
    const [roomFilter, setRoomFilter] = useState<'all' | 'available' | 'full'>('all')
    const [movePopup, setMovePopup] = useState<string | null>(null)

    useEffect(() => {
        if (!movePopup) return
        const handler = () => setMovePopup(null)
        document.addEventListener('click', handler)
        return () => document.removeEventListener('click', handler)
    }, [movePopup])
    const [createModal, setCreateModal] = useState(false)
    const [newRoomNumber, setNewRoomNumber] = useState('')
    const [newRoomCapacity, setNewRoomCapacity] = useState(2)
    const [newRoomNotes, setNewRoomNotes] = useState('')
    const [createError, setCreateError] = useState<string | null>(null)

    const refresh = useCallback(async () => {
        if (!batchId) return
        setState((s) => ({ ...s, isLoading: true, error: null }))
        try {
            const [roomsRes, enrollmentsRes] = await Promise.all([
                roomService.listByBatch(batchId),
                roomService.listBatchEnrollments(batchId),
            ])

            setState((s) => ({
                ...s,
                isLoading: false,
                rooms: roomsRes.data?.rooms ?? [],
                assignments: roomsRes.data?.assignments ?? [],
                enrollments: enrollmentsRes.data ?? [],
            }))
        } catch (err: unknown) {
            setState((s) => ({
                ...s,
                isLoading: false,
                error: err instanceof Error ? err.message : 'Gagal memuat data',
            }))
        }
    }, [batchId])

    useEffect(() => {
        refresh()
    }, [refresh])

    const getEnrollmentInfo = (enrollmentId: string): BatchEnrollment | undefined => {
        return state.enrollments.find((e) => e.enrollmentId === enrollmentId)
    }

    const handleCreateRoom = async () => {
        if (!batchId || !newRoomNumber.trim()) return
        setCreateError(null)
        setIsUpdating(true)
        try {
            await roomService.createRoom(batchId, newRoomNumber.trim(), newRoomCapacity, newRoomNotes.trim() || undefined)
            setCreateModal(false)
            setNewRoomNumber('')
            setNewRoomCapacity(2)
            setNewRoomNotes('')
            await refresh()
        } catch (err: unknown) {
            setCreateError(err instanceof Error ? err.message : 'Gagal membuat kamar')
        } finally {
            setIsUpdating(false)
        }
    }

    const handleAutoAssign = async (overrideExisting: boolean) => {
        if (!batchId) return
        setConfirmOverride(false)
        setIsUpdating(true)
        const names = customRoomNames
            .split('\n')
            .map((n) => n.trim())
            .filter((n) => n.length > 0)
        setCustomRoomNames('')
        try {
            const res = await roomService.autoAssign(batchId, overrideExisting, names.length > 0 ? names : undefined)
            setState((s) => ({ ...s, warnings: res.data?.warnings ?? [] }))
            await refresh()
        } catch (err: unknown) {
            setState((s) => ({
                ...s,
                error: err instanceof Error ? err.message : 'Auto-assign gagal',
            }))
        } finally {
            setIsUpdating(false)
        }
    }

    const handleRemoveAssignment = async (assignmentId: string) => {
        if (!window.confirm('Keluarkan peserta ini dari kamar?')) return
        try {
            await roomService.removeAssignment(assignmentId)
            await refresh()
        } catch (err: unknown) {
            setState(s => ({ ...s, error: err instanceof Error ? err.message : 'Gagal mengeluarkan peserta' }))
        }
    }

    const handleMove = async (assignmentId: string, newRoomId: string) => {
        try {
            await roomService.moveAssignment(assignmentId, newRoomId)
            await refresh()
        } catch (err: unknown) {
            setState(s => ({ ...s, error: err instanceof Error ? err.message : 'Gagal memindahkan peserta' }))
        }
    }

    const handleDeleteRoom = async (roomId: string) => {
        if (!window.confirm('Hapus kamar ini? Semua peserta di kamar ini akan dilepas (kembali ke belum terassign).')) return
        try {
            await roomService.deleteRoom(roomId)
            await refresh()
        } catch (err: unknown) {
            setState(s => ({ ...s, error: err instanceof Error ? err.message : 'Gagal menghapus kamar' }))
        }
    }

    const handleAddAssignment = async () => {
        if (!addModal || !addEnrollmentId) return
        setAddError(null)
        setIsUpdating(true)
        try {
            await roomService.addAssignment(addModal.roomId, addEnrollmentId, addNotes.trim() || undefined)
            setAddModal(null)
            setAddEnrollmentId('')
            setAddNotes('')
            await refresh()
        } catch (err: unknown) {
            setAddError(err instanceof Error ? err.message : 'Gagal menambahkan peserta')
        } finally {
            setIsUpdating(false)
        }
    }

    const handleQuickAssign = async (enrollmentId: string, roomId: string) => {
        if (!enrollmentId || !roomId) return
        setIsUpdating(true)
        try {
            await roomService.addAssignment(roomId, enrollmentId)
            await refresh()
        } catch (err: unknown) {
            setState(s => ({ ...s, error: err instanceof Error ? err.message : 'Gagal assign peserta' }))
        } finally {
            setIsUpdating(false)
        }
    }

    const handleUpdateCapacity = async () => {
        if (!capacityModal) return
        setIsUpdating(true)
        try {
            await roomService.updateRoom(capacityModal.room.id, capacityModal.room.roomNumber, capacityModal.value, capacityModal.room.notes ?? '')
            setCapacityModal(null)
            await refresh()
        } catch (err: unknown) {
            setState(s => ({ ...s, error: err instanceof Error ? err.message : 'Gagal memperbarui kapasitas' }))
        } finally {
            setIsUpdating(false)
        }
    }

    const handleRename = async () => {
        if (!renameModal || !renameValue.trim()) return
        setIsUpdating(true)
        try {
            await roomService.updateRoom(renameModal.id, renameValue.trim(), renameModal.capacity, renameModal.notes ?? '')
            setRenameModal(null)
            await refresh()
        } catch (err: unknown) {
            setState(s => ({ ...s, error: err instanceof Error ? err.message : 'Gagal mengubah nama kamar' }))
        } finally {
            setIsUpdating(false)
        }
    }

    const assignmentsByRoom: Record<string, RoomAssignment[]> = {}
    for (const a of state.assignments) {
        assignmentsByRoom[a.roomId] = assignmentsByRoom[a.roomId] || []
        assignmentsByRoom[a.roomId].push(a)
    }

    // Unassigned enrollments = those in batch but not yet in any room
    const assignedEnrollmentIds = new Set(state.assignments.map(a => a.enrollmentId))
    const unassignedEnrollments = state.enrollments
        .filter(e => !assignedEnrollmentIds.has(e.enrollmentId))
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))

    // Filtered rooms for display (always sorted by room number)
    const displayedRooms = state.rooms
        .filter((room) => {
            const count = (assignmentsByRoom[room.id]?.length ?? 0)
            if (roomFilter === 'available') return count < room.capacity
            if (roomFilter === 'full') return count >= room.capacity
            return true
        })
        .sort((a, b) => a.roomNumber.localeCompare(b.roomNumber, undefined, { numeric: true }))

    return (
        <div className="space-y-10 pb-32">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/admin/batches')}
                        className="size-10 rounded-xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/5 flex items-center justify-center hover:bg-slate-50 transition-all text-slate-400"
                    >
                        <span className="material-symbols-outlined text-[20px]">arrow_back</span>
                    </button>
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                            <Link to="/admin/batches" className="hover:text-primary transition-colors">Batches</Link>
                            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
                            <span>Kamar & Jadwal</span>
                        </div>
                        <span className="text-xs font-bold text-slate-900 dark:text-white">{(batchData?.data as any)?.title || `Batch ${batchId?.slice(0, 8)}`}</span>
                    </div>
                </div>

                <button
                    onClick={refresh}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-100 dark:border-white/5 text-slate-500 hover:bg-slate-50 dark:hover:bg-white/5 transition-all"
                >
                    <span className={`material-symbols-outlined text-[18px] ${state.isLoading ? 'animate-spin' : ''}`}>refresh</span>
                    <span className="text-xs font-bold">Refresh Data</span>
                </button>
            </div>

            <PageHeader
                title="Kelola Kamar & Penempatan"
                subtitle="Distribusi peserta pelatihan ke dalam kamar-kamar yang tersedia secara otomatis atau manual."
                actions={
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <button
                            onClick={() => { setCreateModal(true); setCreateError(null) }}
                            className="flex items-center gap-2 px-6 py-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-white rounded-2xl font-bold text-sm hover:bg-slate-50 dark:hover:bg-white/5 active:scale-95 transition-all"
                        >
                            <span className="material-symbols-outlined text-[20px]">add</span>
                            Buat Kamar Manual
                        </button>
                        <button
                            data-testid="auto-assign-button"
                            onClick={() => setConfirmOverride(true)}
                            className="flex items-center gap-2 px-6 py-4 bg-primary text-white rounded-2xl font-bold text-sm hover:opacity-90 active:scale-95 transition-all shadow-xl shadow-primary/20"
                        >
                            <span className="material-symbols-outlined text-[20px]">magic_button</span>
                            Auto-Assign Peserta
                        </button>
                    </div>
                }
            />

            <div className="flex items-center gap-4 border-b border-slate-100 dark:border-white/5 mb-8">
                <button
                    onClick={() => setActiveTab('rooms')}
                    className={`pb-4 px-2 text-sm font-bold border-b-2 transition-all ${
                        activeTab === 'rooms' 
                            ? 'border-primary text-primary' 
                            : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                    }`}
                >
                    Kelola Kamar & Penempatan
                </button>
                <button
                    onClick={() => setActiveTab('schedules')}
                    className={`pb-4 px-2 text-sm font-bold border-b-2 transition-all ${
                        activeTab === 'schedules' 
                            ? 'border-primary text-primary' 
                            : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                    }`}
                >
                    Jadwal Sesi Pertemuan
                </button>
            </div>

            {activeTab === 'schedules' && batchId ? (
                <AdminBatchSchedulesTab batchId={batchId} />
            ) : (
                <>
                    {state.error && (
                <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-500 flex items-center gap-3">
                    <span className="material-symbols-outlined text-[20px]">error</span>
                    <span className="text-sm font-bold">{state.error}</span>
                    <button onClick={() => setState(s => ({ ...s, error: null }))} className="ml-auto text-[18px] material-symbols-outlined">close</button>
                </div>
            )}

            {state.enrollments.length === 0 && !state.isLoading && (
                <div className="p-6 rounded-[2rem] bg-amber-500/5 border border-amber-500/10 text-amber-600 dark:text-amber-400 flex items-start gap-4">
                    <span className="material-symbols-outlined text-[24px] mt-0.5">group_off</span>
                    <div className="space-y-1">
                        <p className="text-sm font-bold">Belum Ada Peserta Terdaftar</p>
                        <p className="text-xs font-medium opacity-80 leading-relaxed">
                            Gelombang ini belum memiliki pendaftar. Fitur <b>Auto-Assign</b> membutuhkan data peserta yang sudah <b>Aktif & Lunas</b> untuk mendistribusikan kamar secara otomatis.
                        </p>
                    </div>
                </div>
            )}

            {state.warnings.length > 0 && (
                <div data-testid="warnings-panel" className="p-6 rounded-[2rem] bg-amber-500/5 border border-amber-500/10 text-amber-600 dark:text-amber-400">
                    <div className="flex items-center gap-2 font-bold mb-3">
                        <span className="material-symbols-outlined text-[20px]">warning</span>
                        <span className="text-sm">Beberapa peserta tidak dapat ditempatkan otomatis:</span>
                    </div>
                    <ul className="grid md:grid-cols-2 gap-x-8 gap-y-1 list-inside list-disc text-xs font-medium opacity-80">
                        {state.warnings.map((w, i) => (
                            <li key={i} className="truncate">{w}</li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Gender Summary + Room List + Monitoring Panel */}
            {state.isLoading ? (
                <div className="flex flex-col items-center justify-center py-32 space-y-4">
                    <div className="size-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
                    <p className="text-sm font-bold text-slate-400 tracking-tight">Menyelaraskan data kamar...</p>
                </div>
            ) : state.rooms.length === 0 ? (
                <div data-testid="empty-rooms" className="text-center py-32 bg-white dark:bg-slate-900 rounded-[3rem] border border-dashed border-slate-200 dark:border-white/10">
                    <div className="size-20 bg-slate-100 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-300">
                        <span className="material-symbols-outlined text-4xl">bed</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Kamar Belum Tersedia</h3>
                    <p className="text-sm text-slate-500 font-medium max-w-sm mx-auto leading-relaxed">
                        {state.enrollments.length === 0
                            ? 'Tunggu sampai ada peserta yang mendaftar dan melunasi pembayaran sebelum melakukan pembagian kamar.'
                            : 'Gunakan tombol Auto-Assign di atas untuk membuat distribusi kamar secara otomatis berdasarkan kapasitas batch.'
                        }
                    </p>
                </div>
            ) : (
                <div className="flex flex-col xl:flex-row gap-6">
                    {/* ======== LEFT PANEL: Summary + Room List ======== */}
                    <div className="xl:flex-1 xl:min-w-0 space-y-5">
                        {/* Gender Summary */}
                        <div className="grid grid-cols-3 gap-3">
                            {/* Total */}
                            <div className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-br from-slate-600 to-slate-700 text-white shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group">
                                <div className="absolute top-0 right-0 size-16 rounded-full blur-2xl bg-white/10 -mr-4 -mt-4 group-hover:scale-110 duration-500" />
                                <div className="absolute bottom-0 left-0 size-12 rounded-full blur-xl bg-black/10 -ml-4 -mb-4" />
                                <div className="inline-flex items-center justify-center size-8 rounded-lg bg-white/20 backdrop-blur-md shadow-[inset_0_1px_1px_rgba(255,255,255,0.4)] mb-3 transition-transform group-hover:rotate-6 duration-300">
                                    <span className="material-symbols-outlined text-[18px]">group</span>
                                </div>
                                <div className="text-xs font-black text-white/80 mb-0.5">Total Peserta</div>
                                <div className="text-2xl font-black tracking-tight drop-shadow-md">{state.enrollments.length}</div>
                            </div>

                            {/* Laki-laki */}
                            <div className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group">
                                <div className="absolute top-0 right-0 size-16 rounded-full blur-2xl bg-white/10 -mr-4 -mt-4 group-hover:scale-110 duration-500" />
                                <div className="absolute bottom-0 left-0 size-12 rounded-full blur-xl bg-black/10 -ml-4 -mb-4" />
                                <div className="inline-flex items-center justify-center size-8 rounded-lg bg-white/20 backdrop-blur-md shadow-[inset_0_1px_1px_rgba(255,255,255,0.4)] mb-3 transition-transform group-hover:rotate-6 duration-300">
                                    <span className="material-symbols-outlined text-[18px]">male</span>
                                </div>
                                <div className="text-xs font-black text-white/80 mb-0.5">Laki-laki</div>
                                <div className="text-2xl font-black tracking-tight drop-shadow-md">{state.enrollments.filter(e => e.gender === 'L').length}</div>
                            </div>

                            {/* Perempuan */}
                            <div className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-br from-pink-500 to-pink-600 text-white shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group">
                                <div className="absolute top-0 right-0 size-16 rounded-full blur-2xl bg-white/10 -mr-4 -mt-4 group-hover:scale-110 duration-500" />
                                <div className="absolute bottom-0 left-0 size-12 rounded-full blur-xl bg-black/10 -ml-4 -mb-4" />
                                <div className="inline-flex items-center justify-center size-8 rounded-lg bg-white/20 backdrop-blur-md shadow-[inset_0_1px_1px_rgba(255,255,255,0.4)] mb-3 transition-transform group-hover:rotate-6 duration-300">
                                    <span className="material-symbols-outlined text-[18px]">female</span>
                                </div>
                                <div className="text-xs font-black text-white/80 mb-0.5">Perempuan</div>
                                <div className="text-2xl font-black tracking-tight drop-shadow-md">{state.enrollments.filter(e => e.gender === 'P').length}</div>
                            </div>
                        </div>

                        {/* Compact Room List */}
                        <div className="flex items-center gap-2">
                            <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200 flex-1">Daftar Kamar</h3>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Filter:</span>
                            <select
                                value={roomFilter}
                                onChange={(e) => setRoomFilter(e.target.value as typeof roomFilter)}
                                className="text-xs font-bold bg-white dark:bg-slate-800 border border-slate-300 dark:border-white/15 rounded-lg px-3 py-2 outline-none text-slate-700 dark:text-slate-200 cursor-pointer"
                            >
                                <option value="all">Semua</option>
                                <option value="available">Tersedia</option>
                                <option value="full">Penuh</option>
                            </select>
                        </div>

                        {displayedRooms.length === 0 ? (
                            <div className="text-center py-8 bg-white dark:bg-slate-900 rounded-2xl border border-dashed border-slate-200 dark:border-white/10">
                                <span className="material-symbols-outlined text-[32px] text-slate-300 mb-2 block">filter_none</span>
                                <p className="text-xs font-bold text-slate-400">Tidak ada kamar yang sesuai filter</p>
                            </div>
                        ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {displayedRooms.map((room) => {
                                const roomAssignments = (assignmentsByRoom[room.id] ?? []).slice().sort((a, b) => {
                                    const nameA = getEnrollmentInfo(a.enrollmentId)?.name ?? ''
                                    const nameB = getEnrollmentInfo(b.enrollmentId)?.name ?? ''
                                    return nameA.localeCompare(nameB)
                                })
                                const isFull = roomAssignments.length >= room.capacity

                                return (
                                    <div key={room.id} data-testid={`room-card-${room.roomNumber}`} className="group bg-white dark:bg-slate-900 rounded-2xl border-2 border-slate-300 dark:border-white/20 overflow-hidden hover:shadow-lg hover:border-slate-400 dark:hover:border-white/30 transition-all flex">
                                        {/* Room Number — left side, full height */}
                                        <div className={`shrink-0 w-20 flex items-center justify-center text-center font-extrabold text-sm border-r-2 leading-tight ${isFull ? 'bg-rose-500 border-rose-600 text-white dark:border-rose-400' : 'bg-primary border-primary/80 text-white'}`}>
                                            {room.roomNumber}
                                        </div>

                                        {/* Content — right side */}
                                        <div className="flex-1 min-w-0 flex flex-col">
                                            {/* Header row: capacity + status + actions */}
                                            <div className="flex items-center gap-2 px-4 py-2.5">
                                                <span className="text-xs font-bold text-slate-800 dark:text-slate-200">{roomAssignments.length}/{room.capacity}</span>
                                                <span className={`size-2 rounded-full ${isFull ? 'bg-rose-500' : 'bg-emerald-500'}`} />
                                                <span className={`text-[11px] font-bold ${isFull ? 'text-rose-600' : 'text-emerald-600'}`}>{isFull ? 'Penuh' : 'Tersedia'}</span>
                                                {room.notes && <span className="text-[10px] text-slate-500 dark:text-slate-400 truncate ml-1">· {room.notes}</span>}
                                                <div className="ml-auto flex gap-0.5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                                    <button onClick={() => { setRenameModal(room); setRenameValue(room.roomNumber) }} className="size-7 rounded-md hover:bg-slate-200 dark:hover:bg-white/15 flex items-center justify-center text-slate-500 hover:text-slate-800 dark:hover:text-white transition-all" title="Ubah nama kamar">
                                                        <span className="material-symbols-outlined text-[17px]">edit</span>
                                                    </button>
                                                    <button onClick={() => setCapacityModal({ room, value: room.capacity })} className="size-7 rounded-md hover:bg-slate-200 dark:hover:bg-white/15 flex items-center justify-center text-slate-500 hover:text-slate-800 dark:hover:text-white transition-all" title="Edit kapasitas">
                                                        <span className="material-symbols-outlined text-[17px]">settings</span>
                                                    </button>
                                                    <button onClick={() => handleDeleteRoom(room.id)} className="size-7 rounded-md hover:bg-rose-200 dark:hover:bg-rose-500/30 flex items-center justify-center text-slate-400 hover:text-rose-600 transition-all" title="Hapus kamar">
                                                        <span className="material-symbols-outlined text-[17px]">delete</span>
                                                    </button>
                                                </div>
                                            </div>

                                            <hr className="border-slate-200 dark:border-white/15" />

                                            {/* Occupants */}
                                            <div className="flex-1">
                                                {roomAssignments.length === 0 ? (
                                                    <p className="text-xs text-slate-400 px-4 py-4">Belum ada penghuni</p>
                                                ) : (
                                                    roomAssignments.map((a, i) => {
                                                        const info = getEnrollmentInfo(a.enrollmentId)
                                                        const gender = info?.gender
                                                        return (
                                                            <div key={a.id}>
                                                                {i > 0 && <hr className="border-slate-150 dark:border-white/8" />}
                                                                <div className="flex items-center gap-2 px-4 py-2.5">
                                                                    <span className="flex-1 text-[13px] font-semibold text-slate-800 dark:text-slate-100 truncate">{info?.name ?? '...'}</span>
                                                                    {gender && (
                                                                        <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded ${gender === 'L' ? 'bg-blue-500 text-white' : 'bg-pink-500 text-white'}`}>
                                                                            {gender === 'L' ? 'L' : 'P'}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )
                                                    })
                                                )}
                                            </div>

                                            {!isFull && (
                                                <>
                                                    <hr className="border-slate-200 dark:border-white/15" />
                                                    <button
                                                        onClick={() => {
                                                            setAddModal({ roomId: room.id, roomNumber: room.roomNumber })
                                                            setAddEnrollmentId('')
                                                            setAddNotes('')
                                                            setAddError(null)
                                                        }}
                                                        className="w-full px-4 py-2.5 text-xs font-bold text-slate-500 hover:text-primary hover:bg-primary/5 dark:hover:bg-white/5 transition-all flex items-center justify-center gap-1.5"
                                                    >
                                                        <span className="material-symbols-outlined text-[17px]">add</span>
                                                        Tambah Peserta
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                        )}
                    </div>

                    {/* ======== RIGHT PANEL: Monitoring Peserta ======== */}
                    <div className="xl:w-[360px] xl:shrink-0 space-y-5">
                        {/* Assigned Participants */}
                        <div className="bg-white dark:bg-slate-900 rounded-2xl border-2 border-slate-300 dark:border-white/20 overflow-hidden">
                            <div className="flex items-center gap-2 px-5 py-3.5 border-b-2 border-slate-200 dark:border-white/15">
                                <span className="material-symbols-outlined text-[20px] text-primary">groups</span>
                                <h3 className="text-[13px] font-bold text-slate-800 dark:text-slate-100">Peserta Terassign</h3>
                                <span className="ml-auto px-2 py-0.5 rounded-full bg-primary text-white text-[11px] font-bold">{state.assignments.length}</span>
                            </div>

                            {state.assignments.length === 0 ? (
                                <p className="text-center py-10 text-xs text-slate-500 font-medium">Belum ada peserta yang ditempatkan di kamar.</p>
                            ) : (
                                <div>
                                    {state.assignments.map((a, i) => {
                                        const info = getEnrollmentInfo(a.enrollmentId)
                                        const gender = info?.gender
                                        const room = state.rooms.find(r => r.id === a.roomId)

                                        return (
                                            <div key={a.id} className={`relative flex items-center gap-3 px-5 py-2.5 hover:bg-slate-100 dark:hover:bg-white/5 transition-all group/item ${i > 0 ? 'border-t border-slate-200 dark:border-white/10' : ''}`}>
                                                <Avatar name={info?.name ?? '?'} size="sm" />
                                                <span className="flex-1 min-w-0 text-[13px] font-semibold text-slate-800 dark:text-slate-100 truncate">{info?.name ?? '...'}</span>
                                                {gender && (
                                                    <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded ${gender === 'L' ? 'bg-blue-500 text-white' : 'bg-pink-500 text-white'}`}>
                                                        {gender === 'L' ? 'L' : 'P'}
                                                    </span>
                                                )}
                                                <span className="shrink-0 text-[11px] font-bold text-slate-600 dark:text-slate-300 bg-slate-200 dark:bg-white/15 px-1.5 py-0.5 rounded">
                                                    {room?.roomNumber ?? '?'}
                                                </span>
                                                <div className="relative">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setMovePopup(movePopup === a.id ? null : a.id) }}
                                                        className="size-7 rounded-md hover:bg-slate-200 dark:hover:bg-white/15 flex items-center justify-center text-slate-400 hover:text-primary transition-all opacity-0 group-hover/item:opacity-100"
                                                        title="Pindah kamar"
                                                    >
                                                        <span className="material-symbols-outlined text-[16px]">swap_horiz</span>
                                                    </button>
                                                    {movePopup === a.id && (
                                                        <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-slate-800 rounded-xl border-2 border-slate-200 dark:border-white/15 shadow-xl py-1 min-w-[160px]" onClick={(e) => e.stopPropagation()}>
                                                            <div className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase">Pindah ke:</div>
                                                            {state.rooms.map((r) => {
                                                                const isCurrent = r.id === a.roomId
                                                                const occupants = state.assignments.filter(x => x.roomId === r.id).length
                                                                const isFull = occupants >= r.capacity
                                                                return (
                                                                    <button
                                                                        key={r.id}
                                                                        disabled={isCurrent || isFull}
                                                                        onClick={() => { handleMove(a.id, r.id); setMovePopup(null) }}
                                                                        className={`w-full px-3 py-2 text-left text-[12px] flex items-center gap-2 transition-all ${isCurrent ? 'bg-primary/10 text-primary font-bold cursor-default' : isFull ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed' : 'hover:bg-slate-100 dark:hover:bg-white/10 text-slate-700 dark:text-slate-200'}`}
                                                                    >
                                                                        <span className={`size-1.5 rounded-full ${isCurrent ? 'bg-primary' : isFull ? 'bg-slate-300' : 'bg-emerald-500'}`} />
                                                                        <span className="flex-1 truncate">{r.roomNumber}</span>
                                                                        <span className="text-[10px] text-slate-400">{occupants}/{r.capacity}</span>
                                                                    </button>
                                                                )
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                                <button
                                                    onClick={() => handleRemoveAssignment(a.id)}
                                                    className="shrink-0 size-7 rounded-md hover:bg-rose-200 dark:hover:bg-rose-500/30 flex items-center justify-center text-slate-400 hover:text-rose-600 transition-all opacity-0 group-hover/item:opacity-100"
                                                    title="Keluarkan"
                                                >
                                                    <span className="material-symbols-outlined text-[16px]">close</span>
                                                </button>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Unassigned Participants - Green Area */}
                        {unassignedEnrollments.length > 0 && (
                            <div className="bg-emerald-50 dark:bg-emerald-500/5 rounded-2xl border-2 border-emerald-300 dark:border-emerald-500/20 overflow-hidden">
                                <div className="flex items-center gap-2 px-5 py-3.5 border-b-2 border-emerald-200 dark:border-emerald-500/15">
                                    <span className="material-symbols-outlined text-[20px] text-emerald-600 dark:text-emerald-400">person_alert</span>
                                    <h3 className="text-[13px] font-bold text-emerald-700 dark:text-emerald-300">Belum Terassign</h3>
                                    <span className="ml-auto px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[11px] font-bold">{unassignedEnrollments.length}</span>
                                </div>

                                <div>
                                    {unassignedEnrollments.map((e, i) => {
                                        const gender = e.gender
                                        return (
                                            <div key={e.enrollmentId} className={`flex items-center gap-3 px-5 py-2.5 bg-white/80 dark:bg-slate-900/40 ${i > 0 ? 'border-t border-emerald-100 dark:border-emerald-500/8' : ''}`}>
                                                <Avatar name={e.name} size="sm" />
                                                <span className="flex-1 min-w-0 text-[13px] font-semibold text-slate-800 dark:text-slate-100 truncate">{e.name}</span>
                                                {gender && (
                                                    <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded ${gender === 'L' ? 'bg-blue-500 text-white' : 'bg-pink-500 text-white'}`}>
                                                        {gender === 'L' ? 'L' : 'P'}
                                                    </span>
                                                )}
                                                <select
                                                    className="text-[11px] font-bold bg-white dark:bg-slate-800 border-2 border-emerald-400 dark:border-emerald-500/40 rounded-md px-2.5 py-1.5 outline-none text-emerald-700 dark:text-emerald-300 cursor-pointer hover:border-emerald-600 transition-colors"
                                                    defaultValue=""
                                                    onChange={(ev) => {
                                                        if (ev.target.value) {
                                                            handleQuickAssign(e.enrollmentId, ev.target.value)
                                                            ev.target.value = ''
                                                        }
                                                    }}
                                                >
                                                    <option value="" disabled>Assign ke...</option>
                                                    {state.rooms.map((r) => (
                                                        <option key={r.id} value={r.id}>Kamar {r.roomNumber}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Create Room Modal */}
            {createModal && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-[100] p-6" onClick={() => setCreateModal(false)}>
                    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-6 sm:p-10 w-full max-w-md shadow-2xl border border-white/20" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-4 mb-8">
                            <div className="size-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                                <span className="material-symbols-outlined text-primary text-[28px]">meeting_room</span>
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Buat Kamar Baru</h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-1">Tambahkan kamar secara manual</p>
                            </div>
                        </div>

                        <div className="space-y-5">
                            <div className="space-y-2">
                                <label className="text-[13px] font-bold text-slate-700 dark:text-slate-300 ml-1">Nomor / Nama Kamar <span className="text-rose-500">*</span></label>
                                <input
                                    type="text"
                                    value={newRoomNumber}
                                    onChange={(e) => setNewRoomNumber(e.target.value)}
                                    placeholder="Contoh: 101, A1, Melati"
                                    className="w-full px-5 py-4 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent rounded-2xl text-sm font-medium outline-none focus:border-primary/30"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-[13px] font-bold text-slate-700 dark:text-slate-300 ml-1">Kapasitas <span className="text-rose-500">*</span></label>
                                <div className="flex items-center gap-4">
                                    <button onClick={() => setNewRoomCapacity(c => Math.max(1, c - 1))} className="size-12 rounded-xl bg-slate-100 dark:bg-white/5 flex items-center justify-center text-xl font-bold">-</button>
                                    <div className="flex-1 text-center text-3xl font-bold text-primary">{newRoomCapacity}</div>
                                    <button onClick={() => setNewRoomCapacity(c => c + 1)} className="size-12 rounded-xl bg-slate-100 dark:bg-white/5 flex items-center justify-center text-xl font-bold">+</button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[13px] font-bold text-slate-700 dark:text-slate-300 ml-1">Catatan</label>
                                <input
                                    type="text"
                                    value={newRoomNotes}
                                    onChange={(e) => setNewRoomNotes(e.target.value)}
                                    placeholder="Misal: Khusus perempuan, lantai 2"
                                    className="w-full px-5 py-4 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent rounded-2xl text-sm font-medium outline-none focus:border-primary/30"
                                />
                            </div>

                            {createError && (
                                <div className="p-3 bg-rose-500/5 border border-rose-500/10 rounded-xl text-rose-500 text-xs font-bold flex items-center gap-2">
                                    <span className="material-symbols-outlined text-[18px]">error_outline</span>
                                    {createError}
                                </div>
                            )}

                            <div className="flex gap-3 pt-2">
                                <button onClick={() => setCreateModal(false)} className="flex-1 py-4 text-slate-400 font-bold text-sm hover:text-slate-600">Batal</button>
                                <button
                                    onClick={handleCreateRoom}
                                    disabled={!newRoomNumber.trim() || isUpdating}
                                    className="flex-[2] py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl font-bold text-sm shadow-lg shadow-slate-200/50 disabled:opacity-50"
                                >
                                    {isUpdating ? 'Membuat...' : 'Buat Kamar'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Auto-Assign Confirmation Modal */}
            {confirmOverride && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-[100] p-6" onClick={() => { setConfirmOverride(false); setCustomRoomNames('') }}>
                    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-6 sm:p-10 w-full max-w-md shadow-2xl border border-white/20" onClick={(e) => e.stopPropagation()}>
                        <div className="size-16 bg-rose-500/10 rounded-[1.5rem] flex items-center justify-center mb-6">
                            <span className="material-symbols-outlined text-rose-500 text-[32px]">warning</span>
                        </div>
                        <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Konfirmasi Auto-Assign</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 leading-relaxed">Pilih metode penempatan otomatis. Opsi "Override" akan menghapus semua penempatan yang sudah ada.</p>

                        <div className="mb-6">
                            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-2">Nama Kamar (opsional)</label>
                            <textarea
                                value={customRoomNames}
                                onChange={(e) => setCustomRoomNames(e.target.value)}
                                placeholder={"Masukkan nama kamar per baris.\nContoh:\nMelati\nMawar\nAnggrek\n\nKosong = gunakan R1, R2, ..."}
                                className="w-full h-28 px-4 py-3 text-sm border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                            />
                            <p className="text-[10px] text-slate-400 mt-1">Jika nama habis sebelum semua kamar jadi, sisanya otomatis pakai R1, R2, ...</p>
                        </div>

                        <div className="space-y-3">
                            <button data-testid="confirm-override-yes" onClick={() => handleAutoAssign(true)} className="w-full p-5 bg-rose-500 text-white rounded-[1.5rem] font-bold text-sm hover:opacity-90 transition-all flex flex-col items-center">
                                <span>Reset & Override Semua</span>
                                <span className="text-[10px] opacity-70 font-medium">Hapus penempatan lama & buat baru</span>
                            </button>
                            <button data-testid="confirm-override-no" onClick={() => handleAutoAssign(false)} className="w-full p-5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-[1.5rem] font-bold text-sm hover:opacity-90 transition-all flex flex-col items-center">
                                <span>Lengkapi yang Kosong</span>
                                <span className="text-[10px] opacity-60 dark:opacity-40 font-medium">Hanya isi peserta yang belum punya kamar</span>
                            </button>
                            <button onClick={() => { setConfirmOverride(false); setCustomRoomNames('') }} className="w-full p-4 text-slate-400 font-bold text-sm hover:text-slate-600 transition-all">Batal</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Capacity Modal */}
            {capacityModal && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-[100] p-6" onClick={() => setCapacityModal(null)}>
                    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-6 sm:p-10 w-full max-w-sm shadow-2xl border border-white/20" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">Kapasitas Kamar {capacityModal.room.roomNumber}</h3>
                        <div className="space-y-6">
                            <div className="space-y-3">
                                <label className="text-xs font-bold text-slate-500 ml-1">Batas Maksimal Peserta</label>
                                <div className="flex items-center gap-4">
                                    <button onClick={() => setCapacityModal({...capacityModal, value: Math.max(1, capacityModal.value - 1)})} className="size-12 rounded-xl bg-slate-100 dark:bg-white/5 flex items-center justify-center text-xl font-bold">-</button>
                                    <div className="flex-1 text-center text-3xl font-bold text-primary">{capacityModal.value}</div>
                                    <button onClick={() => setCapacityModal({...capacityModal, value: capacityModal.value + 1})} className="size-12 rounded-xl bg-slate-100 dark:bg-white/5 flex items-center justify-center text-xl font-bold">+</button>
                                </div>
                            </div>
                            <button
                                onClick={handleUpdateCapacity}
                                disabled={isUpdating}
                                className="w-full py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl font-bold text-sm shadow-lg shadow-slate-200/50"
                            >
                                {isUpdating ? 'Menyimpan...' : 'Simpan Perubahan'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rename Room Modal */}
            {renameModal && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-[100] p-6" onClick={() => setRenameModal(null)}>
                    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-6 sm:p-10 w-full max-w-sm shadow-2xl border border-white/20" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">Ubah Nama Kamar</h3>
                        <div className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-[13px] font-bold text-slate-700 dark:text-slate-300 ml-1">Nama Kamar <span className="text-rose-500">*</span></label>
                                <input
                                    type="text"
                                    value={renameValue}
                                    onChange={(e) => setRenameValue(e.target.value)}
                                    placeholder="Contoh: 101, A1, Melati"
                                    className="w-full px-5 py-4 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent rounded-2xl text-sm font-medium outline-none focus:border-primary/30"
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleRename() }}
                                    autoFocus
                                />
                            </div>
                            <button
                                onClick={handleRename}
                                disabled={!renameValue.trim() || isUpdating}
                                className="w-full py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl font-bold text-sm shadow-lg shadow-slate-200/50 disabled:opacity-50"
                            >
                                {isUpdating ? 'Menyimpan...' : 'Simpan Perubahan'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Participant Modal */}
            {addModal && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-[100] p-6" onClick={() => setAddModal(null)}>
                    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-6 sm:p-10 w-full max-w-md shadow-2xl border border-white/20" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-4 mb-8">
                            <div className="size-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                                <span className="material-symbols-outlined text-primary text-[28px]">person_add</span>
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Tambah ke Kamar {addModal.roomNumber}</h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-1">Pilih peserta yang belum mendapatkan kamar</p>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-[13px] font-bold text-slate-700 dark:text-slate-300 ml-1">Nama Peserta</label>
                                <select
                                    value={addEnrollmentId}
                                    onChange={(e) => setAddEnrollmentId(e.target.value)}
                                    className="w-full px-5 py-4 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent rounded-2xl text-sm font-bold outline-none focus:border-primary/30"
                                >
                                    <option value="">-- Pilih Peserta --</option>
                                    {unassignedEnrollments.length === 0 ? (
                                        <option disabled>Semua peserta sudah mendapat kamar</option>
                                    ) : (
                                        unassignedEnrollments.map(e => (
                                            <option key={e.enrollmentId} value={e.enrollmentId}>
                                                {e.name} ({e.email}){e.gender ? ` · ${e.gender}` : ''}
                                            </option>
                                        ))
                                    )}
                                </select>
                                {unassignedEnrollments.length === 0 && (
                                    <p className="text-[10px] text-amber-500 font-bold mt-1 ml-1">Semua pendaftar di batch ini sudah mendapat kamar.</p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <label className="text-[13px] font-bold text-slate-700 dark:text-slate-300 ml-1">Catatan</label>
                                <input
                                    type="text"
                                    value={addNotes}
                                    onChange={(e) => setAddNotes(e.target.value)}
                                    placeholder="Misal: Kebutuhan khusus"
                                    className="w-full px-5 py-4 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent rounded-2xl text-sm font-medium outline-none focus:border-primary/30"
                                />
                            </div>

                            {addError && (
                                <div className="p-3 bg-rose-500/5 border border-rose-500/10 rounded-xl text-rose-500 text-xs font-bold flex items-center gap-2">
                                    <span className="material-symbols-outlined text-[18px]">error_outline</span>
                                    {addError}
                                </div>
                            )}

                            <div className="flex gap-3 pt-2">
                                <button onClick={() => setAddModal(null)} className="flex-1 py-4 text-slate-400 font-bold text-sm hover:text-slate-600">Batal</button>
                                <button
                                    onClick={handleAddAssignment}
                                    disabled={!addEnrollmentId || isUpdating}
                                    className="flex-[2] py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl font-bold text-sm shadow-lg shadow-slate-200/50 disabled:opacity-50"
                                >
                                    {isUpdating ? 'Menambahkan...' : 'Tambahkan'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
                </>
            )}
        </div>
    )
}

export default AdminBatchRoomsPage
